import { SyntheticEvent, use, useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { SubscribableOrganisationUnit } from 'features/care-connect-settings/domain/models/subscribable-organisation-unit-tree-response';

export type CareConnectOrganisationUnitTree = {
  organisationUnits: SubscribableOrganisationUnit[] | undefined;
  expandedNodes: string[];
  selectedOrganisationUnitId: string | null;
  handleOnItemClick: (event: SyntheticEvent, itemId: string) => void;
};

const useCareConnectOrganisationUnitTree = (): CareConnectOrganisationUnitTree => {
  const { subscribableOrganisationUnitTreeData, selectedOrganisationUnit, selectOrganisationUnit } =
    use(CareConnectSettingsProviderContext);

  const organisationUnits = subscribableOrganisationUnitTreeData?.organisationUnits;

  const [expandedNodes, setExpandedNodes] = useSessionStorage<string[]>(FilterSessionStorageKeys.CARE_CONNECT_SETTINGS_EXPANDED_NODES, []);

  const collectRootNodeIds = (nodes: SubscribableOrganisationUnit[]): string[] =>
    nodes.flatMap((node) => [...(node.isRoot ? [node.id] : []), ...collectRootNodeIds(node.childSubscriptionChannels)]);

  useEffect(() => {
    if (organisationUnits) {
      const rootIds = collectRootNodeIds(organisationUnits);
      const missingRootIds = rootIds.filter((id) => !expandedNodes.includes(id));
      if (missingRootIds.length > 0) {
        setExpandedNodes((prev) => [...prev, ...missingRootIds]);
      }
    }
  }, [organisationUnits]);

  const findNode = (nodes: SubscribableOrganisationUnit[], id: string): SubscribableOrganisationUnit | undefined => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findNode(node.childSubscriptionChannels, id);
      if (found) return found;
    }
    return undefined;
  };

  const isArrowClick = (event: SyntheticEvent): boolean =>
    event.target instanceof SVGElement || (event.target as HTMLElement).className === 'MuiTreeItem-iconContainer';

  const collectDescendantIds = (node: SubscribableOrganisationUnit): string[] =>
    node.childSubscriptionChannels.flatMap((child) => [child.id, ...collectDescendantIds(child)]);

  const handleOnItemClick = (event: SyntheticEvent, itemId: string): void => {
    if (isArrowClick(event)) {
      const node = findNode(organisationUnits ?? [], itemId);
      if (node?.isRoot) return;
      setExpandedNodes((prev) => {
        if (prev.includes(itemId)) {
          const descendants = node ? collectDescendantIds(node) : [];
          return prev.filter((id) => id !== itemId && !descendants.includes(id));
        }
        return [...prev, itemId, ...(node ? collectDescendantIds(node) : [])];
      });
    } else {
      const node = findNode(organisationUnits ?? [], itemId);
      if (node) {
        selectOrganisationUnit(node);
      }
    }
  };

  return {
    organisationUnits,
    expandedNodes,
    selectedOrganisationUnitId: selectedOrganisationUnit?.id ?? null,
    handleOnItemClick,
  };
};

export default useCareConnectOrganisationUnitTree;
